import {Component, EventEmitter, OnInit, Output} from '@angular/core';
import {ClientConfigurationService, ComponentDeclarationService} from '@twpub/core/services';
import {ClientConfiguration, ClientConfigurationScheme} from '@twpub/core/utils';
import {NGXLogger} from 'ngx-logger';
import {ConfirmDialogComponent} from '../confirm-dialog/confirm-dialog.component';
import {MatDialog} from '@angular/material/dialog';
import {UploadDialogComponent} from '../confirm-dialog/upload-dialog/upload-dialog.component';
import {concatMap, switchMap, tap} from 'rxjs';
import {ALL_COMP_CATEGORIES, ComponentCategory} from '@twpub/core/enums';
import {ImageFileService} from '@twpub/core/services/image-file.service';
import {
  MatCell,
  MatCellDef,
  MatColumnDef,
  MatHeaderCell,
  MatHeaderCellDef,
  MatHeaderRow,
  MatHeaderRowDef,
  MatRow,
  MatRowDef,
  MatTable
} from '@angular/material/table';
import {IconButtonComponent} from '../icon-button/icon-button.component';
import {NgClass} from '@angular/common';
import {MatButton} from '@angular/material/button';
import {MatIcon} from '@angular/material/icon';

/**
 * Container for various components
 */
@Component({
  selector: 'pub-configuration-list',
  templateUrl: './configuration-list.component.html',
  styleUrls: ['./configuration-list.component.scss'],
  standalone: true,
  imports: [MatTable, MatColumnDef, MatHeaderCellDef, MatHeaderCell, MatCellDef, MatCell, IconButtonComponent, MatHeaderRowDef, MatHeaderRow, MatRowDef, MatRow, NgClass, MatButton, MatIcon]
})
export class ConfigurationListComponent implements OnInit {
  @Output() setEditConfig = new EventEmitter<ClientConfiguration>();

  configurations: ClientConfiguration[] = [];
  defaultId: number = 0;
  displayedColumns: string[] = ['id', 'name', 'selected', 'actions'];

  constructor(private logger: NGXLogger, private dialog: MatDialog, private configService: ClientConfigurationService,
              private compService: ComponentDeclarationService, private imageService: ImageFileService) {
  }

  ngOnInit(): void {
    this.fetchConfigurations();
  }

  private fetchConfigurations() {
    this.configService.getConfigurations().pipe(
      tap(configs => this.configurations = configs),
      concatMap(() => this.configService.getDefaultConfiguration(undefined)))
      .subscribe({
        next: config => {
          this.defaultId = config.id;
        }
      });
  }

  editConfig(id: number) {
    const config = this.getConfig(id);
    this.setEditConfig.emit(config?.clone());
  }

  copyConfig(id: number) {
    const config = this.getConfig(id);
    if (config) {
      const newConfig = config.clone();
      newConfig.id = 0;
      newConfig.name = this.findUniqueName(config);
      this.setEditConfig.emit(newConfig);
    }
  }

  private findUniqueName(config: ClientConfiguration) {
    let count = 0;
    let name: string;
    do {
      count++;
      const newSuffix = ' (' + count + ')';
      name = config.name + newSuffix;
    } while (this.configurations.some(config => config.name.toLowerCase() === name.toLowerCase()));
    return name;
  }

  selectConfig(config: ClientConfiguration) {
    config.defaultConfig = true;
    this.configService.updateConfiguration(config).subscribe(() => {
      this.defaultId = config.id;
    })
  }

  createConfig() {
    const scheme = ClientConfigurationScheme.DEFAULT;
    const config = new ClientConfiguration(0, '', scheme);
    this.initDefaultValues(config, scheme);
    this.setEditConfig.emit(config);
  }

  private initDefaultValues(config: ClientConfiguration, scheme: string) {
    ALL_COMP_CATEGORIES.forEach((type) => {
      const compConfigs = this.compService.getDefaultConfigurations(type, scheme);
      config.setConfiguration(type, compConfigs[0]);
    })
  }

  deleteConfig(id: number) {
    const config = this.getConfig(id);
    const message: string[] = ['Are you sure you want to delete the configuration "' + config?.name + '"?', 'This action cannot be undone.'];
    const confirmDialogRef = this.dialog.open(ConfirmDialogComponent, {data: {message, confirmLabel: 'Delete'}});

    confirmDialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (confirmed) {
        this.configService.deleteConfiguration(id).subscribe(() => {
          this.configurations = this.configurations.filter(config => config.id !== id);
        })
      }
    });
  }

  downloadConfig(id: number) {
    const config = this.getConfig(id);
    if (config) {
      // Check if config has a logo image
      const logoConfig = config.getConfigurationSafe(ComponentCategory.Logo);
      const logoImageId = logoConfig?.getNumber('logoImage');

      if (logoImageId) {
        // Fetch logo image and include in export
        this.imageService.getImageFile(logoImageId).subscribe({
          next: (imageFile) => {
            const content = imageFile.content as unknown as string;
            // Estimate base64 size (base64 is ~1.33x the binary size)
            const estimatedSize = content.length * 0.75;
            if (estimatedSize > 1024 * 1024) {
              const kbSize = Math.round(estimatedSize / 1024);
              this.logger.warn(`Logo image is larger than 1MB (${kbSize}KB). Consider using a smaller image.`);
              const warningDialog = this.dialog.open(ConfirmDialogComponent, {
                data: {
                  message: [`The logo image is ${kbSize}KB, which is larger than 1MB.`, 'Consider using a smaller image for better performance.', 'Continue with export?'],
                  confirmLabel: 'Continue'
                }
              });
              warningDialog.afterClosed().subscribe((confirmed: boolean) => {
                if (confirmed) {
                  this.doDownloadConfig(config, {
                    content,
                    mimeType: imageFile.mimeType,
                    name: imageFile.name,
                    width: imageFile.width,
                    height: imageFile.height
                  });
                }
              });
            } else {
              this.doDownloadConfig(config, {
                content,
                mimeType: imageFile.mimeType,
                name: imageFile.name,
                width: imageFile.width,
                height: imageFile.height
              });
            }
          },
          error: (err) => {
            this.logger.warn('Failed to fetch logo image for export, exporting without logo', err);
            this.doDownloadConfig(config);
          }
        });
      } else {
        this.doDownloadConfig(config);
      }
    }
  }

  private doDownloadConfig(config: ClientConfiguration, logoImageData?: {
    content: string;
    mimeType: string;
    name: string;
    width: number;
    height: number
  }) {
    config.logoImageData = logoImageData;
    const dataStr = 'data:text/json;charset=utf-8,' + encodeURIComponent(JSON.stringify(config.toObject()));
    const a = document.createElement('a');
    a.href = dataStr;
    a.download = `twpubConfig-${config.name}.json`;
    a.click();
    // Clear logoImageData from config object (it's only used for export)
    config.logoImageData = undefined;
  }

  importConfig() {
    const confirmDialogRef = this.dialog.open(UploadDialogComponent, {
      data: {
        message: ['Please select a configuration file to upload.'],
        confirmLabel: 'Upload',
        cancelLabel: 'Cancel'
      }
    });

    confirmDialogRef.afterClosed().subscribe((file: File) => {
      if (file) {
        this.handleFileUpload(file);
      }
    });
  }

  private handleFileUpload(file: File): void {
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const config = this.configService.parseClientConfiguration(reader.result as string);
        config.id = 0;
        if (this.configService.getByName(this.configurations, config.name)) {
          config.name = this.findUniqueName(config);
        }

        // If config has logoImageData, upload the logo and update config
        if (config.logoImageData) {
          const logoData = config.logoImageData;
          this.imageService.saveLogoFromBase64(logoData.content, logoData.mimeType, logoData.name, logoData.width, logoData.height)
            .pipe(
              switchMap((imageFile) => {
                // Update dimensions to match the exported config
                return this.imageService.updateDimensions(imageFile.id!, logoData.width, logoData.height);
              })
            )
            .subscribe({
              next: (imageFile) => {
                // Update the LOGO component config with the new file ID
                const logoConfig = config.getConfigurationSafe(ComponentCategory.Logo);
                if (logoConfig) {
                  logoConfig.setConfigValue('logoImage', imageFile.id);
                }
                // Clear logoImageData as it's no longer needed
                config.logoImageData = undefined;
                this.setEditConfig.emit(config);
              },
              error: (err) => {
                this.logger.error('Failed to upload logo image from imported config', err);
                // Still emit the config without the logo
                config.logoImageData = undefined;
                this.setEditConfig.emit(config);
              }
            });
        } else {
          this.setEditConfig.emit(config);
        }
      } catch (error) {
        this.logger.error('Failed to parse configuration file', error);
      }
    };
    reader.readAsText(file);
  }

  private getConfig = (id: number) => this.configurations.find(config => config.id === id);

  isSelected = (config: ClientConfiguration) => config.id === this.defaultId;
}
